import React, { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";

const PhotoManager = () => {
  // =====================================================
  // FORM STATES
  // =====================================================

  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("");

  const [categories, setCategories] = useState([]);
  const [photos, setPhotos] = useState([]);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // =====================================================
  // EDIT STATES
  // =====================================================

  const [editingId, setEditingId] = useState(null);

  // =====================================================
  // SEARCH
  // =====================================================

  const [searchText, setSearchText] = useState("");

  // =====================================================
  // PAGINATION
  // =====================================================

  const [currentPage, setCurrentPage] = useState(1);

  const photosPerPage = 50;

  // =====================================================
  // IMAGE
  // =====================================================

  const [image, setImage] = useState("");
  const [imagePreview, setImagePreview] = useState("");

  // =====================================================
  // HOVER
  // =====================================================

  const [hoveredButton, setHoveredButton] = useState(null);

  // =====================================================
  // LOAD CATEGORIES
  // =====================================================

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const snapshot = await getDocs(
          collection(db, "categories")
        );

        const data = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort(
            (a, b) =>
              Number(a.order || 0) -
              Number(b.order || 0)
          );

        setCategories(data);

        if (data.length > 0) {
          setCategory(data[0].name);
        }
      } catch (error) {
        console.error(
          "Category load error:",
          error
        );
      }
    };

    fetchCategories();
  }, []);

  // =====================================================
  // LOAD PHOTOS
  // =====================================================

  const fetchPhotos = async () => {
    try {
      const q = query(
        collection(db, "photos"),
        orderBy("timestamp", "desc")
      );

      const snapshot = await getDocs(q);

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPhotos(data);
    } catch (error) {
      console.error(
        "Photo load error:",
        error
      );
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  // =====================================================
  // IMAGE UPLOAD - IMGBB
  // =====================================================

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();

      formData.append("image", file);

      const apiKey =
        "874aba0b22e62f51c225a6c83f3f6da5";

      const response = await fetch(
        `https://api.imgbb.com/1/upload?key=${apiKey}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(
          "Image upload failed"
        );
      }

      const imageUrl = data.data.url;

      setImage(imageUrl);
      setImagePreview(imageUrl);

      alert("ছবি সফলভাবে আপলোড হয়েছে!");
    } catch (error) {
      console.error(
        "Image upload error:",
        error
      );

      alert("ছবি আপলোড করতে সমস্যা হয়েছে!");
    } finally {
      setUploading(false);
    }
  };

  // =====================================================
  // ADD / UPDATE PHOTO
  // =====================================================

  const handleSavePhoto = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      alert("ছবির শিরোনাম দিন!");
      return;
    }

    if (!image) {
      alert("একটি ছবি আপলোড করুন!");
      return;
    }

    if (!category) {
      alert("ক্যাটাগরি নির্বাচন করুন!");
      return;
    }

    setLoading(true);

    try {
      // =================================================
      // UPDATE
      // =================================================

      if (editingId) {
        await updateDoc(
          doc(db, "photos", editingId),
          {
            title: title.trim(),
            caption: caption.trim(),
            category: category,
            image: image,
            updatedAt: serverTimestamp(),
          }
        );

        alert("ছবির তথ্য আপডেট হয়েছে!");
      }

      // =================================================
      // ADD
      // =================================================

      else {
        await addDoc(
          collection(db, "photos"),
          {
            title: title.trim(),
            caption: caption.trim(),
            category: category,
            image: image,
            status: "active",
            timestamp: serverTimestamp(),
          }
        );

        alert("ছবি সফলভাবে প্রকাশিত হয়েছে!");
      }

      // =================================================
      // RESET
      // =================================================

      resetForm();

      await fetchPhotos();
    } catch (error) {
      console.error(
        "Photo save error:",
        error
      );

      alert(
        editingId
          ? "ছবি আপডেট করতে সমস্যা হয়েছে!"
          : "ছবি প্রকাশ করতে সমস্যা হয়েছে!"
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // RESET FORM
  // =====================================================

  const resetForm = () => {
    setTitle("");
    setCaption("");
    setImage("");
    setImagePreview("");

    setEditingId(null);

    if (categories.length > 0) {
      setCategory(categories[0].name);
    }
  };

  // =====================================================
  // EDIT PHOTO
  // =====================================================

  const handleEdit = (photo) => {
    setEditingId(photo.id);

    setTitle(photo.title || "");
    setCaption(photo.caption || "");
    setCategory(photo.category || "");

    setImage(photo.image || "");
    setImagePreview(photo.image || "");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =====================================================
  // CANCEL EDIT
  // =====================================================

  const handleCancelEdit = () => {
    resetForm();
  };

  // =====================================================
  // DELETE PHOTO
  // =====================================================

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "আপনি কি এই ছবিটি ডিলিট করতে চান?"
    );

    if (!confirmDelete) return;

    try {
      await deleteDoc(
        doc(db, "photos", id)
      );

      setPhotos((prev) =>
        prev.filter(
          (photo) => photo.id !== id
        )
      );

      alert("ছবি ডিলিট হয়েছে!");
    } catch (error) {
      console.error(
        "Delete error:",
        error
      );

      alert(
        "ছবি ডিলিট করতে সমস্যা হয়েছে!"
      );
    }
  };

  // =====================================================
  // SEARCH FILTER
  // =====================================================

  const filteredPhotos = photos.filter(
    (photo) => {
      const search =
        searchText
          .trim()
          .toLowerCase();

      if (!search) return true;

      return (
        (photo.title || "")
          .toLowerCase()
          .includes(search) ||
        (photo.category || "")
          .toLowerCase()
          .includes(search) ||
        (photo.caption || "")
          .toLowerCase()
          .includes(search)
      );
    }
  );

  // =====================================================
  // PAGINATION
  // =====================================================

  const totalPages = Math.ceil(
    filteredPhotos.length /
      photosPerPage
  );

  const indexOfLastPhoto =
    currentPage * photosPerPage;

  const indexOfFirstPhoto =
    indexOfLastPhoto -
    photosPerPage;

  const currentPhotos =
    filteredPhotos.slice(
      indexOfFirstPhoto,
      indexOfLastPhoto
    );

  // =====================================================
  // SEARCH CHANGE
  // =====================================================

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  // =====================================================
  // DATE FORMAT
  // =====================================================

  const formatDate = (timestamp) => {
    if (!timestamp) return "";

    const date = timestamp.seconds
      ? new Date(
          timestamp.seconds * 1000
        )
      : new Date(timestamp);

    return date.toLocaleDateString(
      "bn-BD",
      {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }
    );
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div
      style={{
        padding: "10px",
      }}
    >

      {/* =================================================
          HEADING
      ================================================= */}

      <h3
        style={{
          marginBottom: "20px",
          borderBottom:
            "2px solid #d2202a",
          paddingBottom: "10px",
        }}
      >
        ফটো ম্যানেজমেন্ট
      </h3>

      {/* =================================================
          ADD / EDIT FORM
      ================================================= */}

      <form
        onSubmit={handleSavePhoto}
        style={{
          display: "grid",
          gridTemplateColumns:
            "1fr 1fr",
          gap: "15px",
          marginBottom: "30px",
        }}
      >

        {/* TITLE */}

        <input
          type="text"
          placeholder="ছবির শিরোনাম"
          value={title}
          onChange={(e) =>
            setTitle(e.target.value)
          }
          style={{
            padding: "12px",
            fontSize: "16px",
            border:
              "1px solid #ccc",
            borderRadius: "4px",
            boxSizing:
              "border-box",
          }}
        />

        {/* CATEGORY */}

        <select
          value={category}
          onChange={(e) =>
            setCategory(
              e.target.value
            )
          }
          style={{
            padding: "12px",
            fontSize: "16px",
            border:
              "1px solid #ccc",
            borderRadius: "4px",
            background: "#fff",
          }}
        >
          {categories.map(
            (cat) => (
              <option
                key={cat.id}
                value={cat.name}
              >
                {cat.name}
              </option>
            )
          )}
        </select>

        {/* IMAGE */}

        <div
          style={{
            border:
              "1px dashed #ccc",
            borderRadius: "4px",
            padding: "10px",
          }}
        >
          <input
            type="file"
            accept="image/*"
            onChange={
              handleImageUpload
            }
            style={{
              width: "100%",
            }}
          />

          {uploading && (
            <div
              style={{
                marginTop: "8px",
                color: "#777",
                fontSize: "14px",
              }}
            >
              ছবি আপলোড হচ্ছে...
            </div>
          )}

          {imagePreview && (
            <img
              src={imagePreview}
              alt="Preview"
              style={{
                width: "120px",
                height: "80px",
                objectFit: "cover",
                marginTop: "10px",
                borderRadius: "4px",
                border:
                  "1px solid #ddd",
              }}
            />
          )}
        </div>

        {/* CAPTION */}

        <input
          type="text"
          placeholder="ছবির ক্যাপশন"
          value={caption}
          onChange={(e) =>
            setCaption(
              e.target.value
            )
          }
          style={{
            padding: "12px",
            fontSize: "16px",
            border:
              "1px solid #ccc",
            borderRadius: "4px",
            boxSizing:
              "border-box",
          }}
        />

        {/* BUTTONS */}

        <div
          style={{
            gridColumn:
              "1 / -1",
            display: "flex",
            gap: "10px",
          }}
        >

          <button
            type="submit"
            disabled={
              loading ||
              uploading
            }
            onMouseEnter={() =>
              setHoveredButton(
                "save"
              )
            }
            onMouseLeave={() =>
              setHoveredButton(null)
            }
            style={{
              flex: 1,
              padding: "13px",
              background:
                loading ||
                uploading
                  ? "#888"
                  : hoveredButton ===
                    "save"
                  ? "#8b0000"
                  : "#666",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor:
                loading ||
                uploading
                  ? "not-allowed"
                  : "pointer",
              fontSize: "17px",
              fontWeight:
                "bold",
              transition:
                "all 0.2s ease",
            }}
          >
            {loading
              ? editingId
                ? "আপডেট হচ্ছে..."
                : "প্রকাশ হচ্ছে..."
              : editingId
              ? "আপডেট করুন"
              : "ছবি প্রকাশ করুন"}
          </button>

          {/* CANCEL */}

          {editingId && (
            <button
              type="button"
              onClick={
                handleCancelEdit
              }
              onMouseEnter={() =>
                setHoveredButton(
                  "cancel"
                )
              }
              onMouseLeave={() =>
                setHoveredButton(null)
              }
              style={{
                padding:
                  "12px 18px",
                background:
                  hoveredButton ===
                  "cancel"
                    ? "#8b0000"
                    : "#666",
                color: "#fff",
                border: "none",
                borderRadius:
                  "4px",
                cursor:
                  "pointer",
                fontWeight:
                  "bold",
                transition:
                  "all 0.2s ease",
              }}
            >
              বাতিল
            </button>
          )}
        </div>
      </form>

      {/* =================================================
          PUBLISHED PHOTO LIST + SEARCH
      ================================================= */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          gap: "15px",
          marginBottom: "15px",
          flexWrap: "wrap",
        }}
      >

        <h4
          style={{
            borderLeft:
              "5px solid #d2202a",
            paddingLeft: "10px",
            margin: 0,
          }}
        >
          প্রকাশিত ছবি
        </h4>

        {/* SEARCH */}

        <input
          type="text"
          placeholder="ছবি খুঁজুন..."
          value={searchText}
          onChange={
            handleSearchChange
          }
          style={{
            width: "280px",
            maxWidth: "100%",
            padding:
              "9px 12px",
            border:
              "1px solid #ccc",
            borderRadius: "4px",
            fontSize: "14px",
            boxSizing:
              "border-box",
          }}
        />
      </div>

      {/* =================================================
          TABLE
      ================================================= */}

      {currentPhotos.length === 0 ? (
        <div
          style={{
            padding: "35px",
            textAlign: "center",
            color: "#777",
            border:
              "1px solid #eee",
            borderRadius: "4px",
          }}
        >
          {searchText
            ? "কোনো ছবি পাওয়া যায়নি।"
            : "এখনও কোনো ছবি প্রকাশিত হয়নি।"}
        </div>
      ) : (
        <div
          style={{
            overflowX: "auto",
            border:
              "1px solid #ddd",
            borderRadius: "5px",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse:
                "collapse",
              minWidth:
                "850px",
            }}
          >
            <thead>
              <tr
                style={{
                  background:
                    "#f5f5f5",
                }}
              >
                <th
                  style={{
                    padding:
                      "11px 10px",
                    borderBottom:
                      "1px solid #ddd",
                    width: "60px",
                  }}
                >
                  SL
                </th>

                <th
                  style={{
                    padding:
                      "11px 10px",
                    borderBottom:
                      "1px solid #ddd",
                    width: "130px",
                  }}
                >
                  প্রকাশের তারিখ
                </th>

                <th
                  style={{
                    padding:
                      "11px 10px",
                    borderBottom:
                      "1px solid #ddd",
                    textAlign:
                      "left",
                  }}
                >
                  শিরোনাম
                </th>

                <th
                  style={{
                    padding:
                      "11px 10px",
                    borderBottom:
                      "1px solid #ddd",
                    width: "130px",
                  }}
                >
                  থাম্বনেইল
                </th>

                <th
                  style={{
                    padding:
                      "11px 10px",
                    borderBottom:
                      "1px solid #ddd",
                    width: "130px",
                  }}
                >
                  ক্যাটাগরি
                </th>

                <th
                  style={{
                    padding:
                      "11px 10px",
                    borderBottom:
                      "1px solid #ddd",
                    width: "150px",
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {currentPhotos.map(
                (photo, index) => (
                  <tr
                    key={photo.id}
                    style={{
                      borderBottom:
                        "1px solid #eee",
                    }}
                  >

                    {/* SL */}

                    <td
                      style={{
                        padding:
                          "10px",
                        textAlign:
                          "center",
                      }}
                    >
                      {indexOfFirstPhoto +
                        index +
                        1}
                    </td>

                    {/* DATE */}

                    <td
                      style={{
                        padding:
                          "10px",
                        textAlign:
                          "center",
                        fontSize:
                          "13px",
                        color:
                          "#555",
                      }}
                    >
                      {formatDate(
                        photo.timestamp
                      )}
                    </td>

                    {/* TITLE */}

                    <td
                      style={{
                        padding:
                          "10px",
                        fontWeight:
                          "600",
                        lineHeight:
                          "1.5",
                      }}
                    >
                      {photo.title}
                    </td>

                    {/* THUMBNAIL */}

                    <td
                      style={{
                        padding:
                          "10px",
                        textAlign:
                          "center",
                      }}
                    >
                      {photo.image ? (
                        <img
                          src={
                            photo.image
                          }
                          alt={
                            photo.title
                          }
                          style={{
                            width:
                              "100px",
                            height:
                              "65px",
                            objectFit:
                              "cover",
                            borderRadius:
                              "4px",
                            border:
                              "1px solid #ddd",
                          }}
                        />
                      ) : (
                        "—"
                      )}
                    </td>

                    {/* CATEGORY */}

                    <td
                      style={{
                        padding:
                          "10px",
                        textAlign:
                          "center",
                      }}
                    >
                      <span
                        style={{
                          display:
                            "inline-block",
                          padding:
                            "5px 9px",
                          background:
                            "#f5f5f5",
                          borderRadius:
                            "4px",
                          fontSize:
                            "13px",
                        }}
                      >
                        {
                          photo.category
                        }
                      </span>
                    </td>

                    {/* ACTION */}

                    <td
                      style={{
                        padding:
                          "10px",
                        textAlign:
                          "center",
                      }}
                    >
                      <div
                        style={{
                          display:
                            "flex",
                          justifyContent:
                            "center",
                          gap: "7px",
                        }}
                      >

                        {/* EDIT */}

                        <button
                          type="button"
                          onClick={() =>
                            handleEdit(
                              photo
                            )
                          }
                          onMouseEnter={() =>
                            setHoveredButton(
                              `edit-${photo.id}`
                            )
                          }
                          onMouseLeave={() =>
                            setHoveredButton(
                              null
                            )
                          }
                          style={{
                            padding:
                              "7px 11px",
                            background:
                              hoveredButton ===
                              `edit-${photo.id}`
                                ? "#4169E1"
                                : "#666",
                            color:
                              "#fff",
                            border:
                              "none",
                            borderRadius:
                              "4px",
                            cursor:
                              "pointer",
                            fontWeight:
                              "bold",
                            transition:
                              "all 0.2s ease",
                          }}
                        >
                          ✏️ Edit
                        </button>

                        {/* DELETE */}

                        <button
                          type="button"
                          onClick={() =>
                            handleDelete(
                              photo.id
                            )
                          }
                          onMouseEnter={() =>
                            setHoveredButton(
                              `delete-${photo.id}`
                            )
                          }
                          onMouseLeave={() =>
                            setHoveredButton(
                              null
                            )
                          }
                          style={{
                            padding:
                              "7px 11px",
                            background:
                              hoveredButton ===
                              `delete-${photo.id}`
                                ? "#8b0000"
                                : "#666",
                            color:
                              "#fff",
                            border:
                              "none",
                            borderRadius:
                              "4px",
                            cursor:
                              "pointer",
                            fontWeight:
                              "bold",
                            transition:
                              "all 0.2s ease",
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* =================================================
          PAGINATION
      ================================================= */}

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent:
              "center",
            alignItems: "center",
            gap: "6px",
            marginTop: "20px",
            flexWrap: "wrap",
          }}
        >

          {/* PREVIOUS */}

          <button
            type="button"
            disabled={
              currentPage === 1
            }
            onClick={() =>
              setCurrentPage(
                (prev) =>
                  prev - 1
              )
            }
            style={{
              padding:
                "8px 12px",
              border:
                "1px solid #ddd",
              borderRadius:
                "4px",
              background:
                currentPage === 1
                  ? "#eee"
                  : "#666",
              color: "#fff",
              cursor:
                currentPage === 1
                  ? "not-allowed"
                  : "pointer",
              fontWeight:
                "bold",
            }}
          >
            ‹
          </button>

          {/* PAGE NUMBERS */}

          {Array.from(
            {
              length:
                totalPages,
            },
            (_, index) =>
              index + 1
          ).map((page) => (
            <button
              type="button"
              key={page}
              onClick={() =>
                setCurrentPage(
                  page
                )
              }
              style={{
                minWidth:
                  "38px",
                padding:
                  "8px 10px",
                border:
                  "1px solid #ddd",
                borderRadius:
                  "4px",
                background:
                  currentPage ===
                  page
                    ? "#d2202a"
                    : "#fff",
                color:
                  currentPage ===
                  page
                    ? "#fff"
                    : "#333",
                cursor:
                  "pointer",
                fontWeight:
                  "bold",
                transition:
                  "all 0.2s ease",
              }}
            >
              {page}
            </button>
          ))}

          {/* NEXT */}

          <button
            type="button"
            disabled={
              currentPage ===
              totalPages
            }
            onClick={() =>
              setCurrentPage(
                (prev) =>
                  prev + 1
              )
            }
            style={{
              padding:
                "8px 12px",
              border:
                "1px solid #ddd",
              borderRadius:
                "4px",
              background:
                currentPage ===
                totalPages
                  ? "#eee"
                  : "#666",
              color: "#fff",
              cursor:
                currentPage ===
                totalPages
                  ? "not-allowed"
                  : "pointer",
              fontWeight:
                "bold",
            }}
          >
            ›
          </button>
        </div>
      )}

      {/* =================================================
          RESPONSIVE
      ================================================= */}

      <style>{`
        @media (max-width: 700px) {
          form {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PhotoManager;
